import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [staff, setStaff]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [assignedBatches, setAssignedBatches] = useState([]); // batch_ids assigned to this staff

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchStaff(session.user.id);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchStaff(session.user.id);
      else { setStaff(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function fetchStaff(userId) {
    const { data } = await supabase.from('staff').select('*').eq('user_id', userId).single();
    setStaff(data);
    // Load batch assignments for this staff member
    if (data?.id) {
      // Match staff.id to hr_staff via email
      const { data: hrStaff } = await supabase.from('hr_staff')
        .select('id')
        .or(`personal_email.eq.${data.email},official_email.eq.${data.email}`)
        .single();
      if (hrStaff?.id) {
        const { data: assignments } = await supabase.from('batch_assignments')
          .select('batch_id')
          .eq('staff_id', hrStaff.id);
        setAssignedBatches((assignments||[]).map(a=>a.batch_id));
      }
    }
    setLoading(false);
  }

  // Real-time: re-fetch staff when their record changes (e.g. permissions updated by admin)
  useEffect(() => {
    if (!staff?.id) return;
    const channel = supabase
      .channel('staff-permissions-' + staff.id)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'staff',
        filter: 'id=eq.' + staff.id,
      }, (payload) => {
        // Permissions or role changed - update staff object immediately
        setStaff(prev => ({ ...prev, ...payload.new }));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [staff?.id]);

  async function refreshStaff() {
    if (user) await fetchStaff(user.id);
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const isEditor = staff?.role === 'editor';
  const isAdmin  = staff?.role === 'admin';
  // Returns true if this staff can edit records for a given batch_id
  function canEditBatch(batchId) {
    if (isAdmin) return true; // admin can edit everything
    // Check if staff has full access via section permissions
    if (getSectionAccess('students') === 'full' && !assignedBatches.length) return true;
    return assignedBatches.includes(batchId);
  }
  // Returns true if staff has any batch assignment (is a coordinator)
  const isCoordinator = assignedBatches.length > 0;

  // Permission levels: 'full' | 'view' | 'hidden'
  // Returns the permission level for a section
  function getSectionAccess(section) {
    const perms = staff?.permissions;
    if (!perms) return isEditor ? 'full' : 'view'; // no perms set = full for editor, view for viewer
    const val = perms[section];
    if (val === 'hidden') return 'hidden';
    if (val === 'view' || val === false) return 'view';
    if (val === 'full' || val === true || val === undefined) return isEditor ? 'full' : 'view';
    return isEditor ? 'full' : 'view';
  }

  // Can the user EDIT this section?
  function canEdit(section) {
    return getSectionAccess(section) === 'full';
  }

  // Can the user SEE this section at all?
  function canView(section) {
    return getSectionAccess(section) !== 'hidden';
  }

  return (
    <AuthContext.Provider value={{ user, staff, loading, signIn, signOut, isEditor, isAdmin, canEdit, canView, getSectionAccess, refreshStaff, assignedBatches, canEditBatch, isCoordinator }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
